













































































































































































































































import {Component, Vue} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';
import {maxLength, required} from 'vuelidate/lib/validators';
import {validationMixin} from 'vuelidate';

import WebAssistantPreview from '../components/WebAssistantPreview.vue';
import WebAssistantViewToggle from '../components/WebAssistantViewToggle.vue';
import ImageUpload from '../components/WebAssistantImageUpload.vue';
import AssetService from '../services/assets';
import {webAssistantApi} from '../services/webAssistant';
import {webAssistantAppService} from '../services/webAssistantAppService';
import {HomescreenAppInitViewModel, HomescreenAppViewModel, WebAssistantViewModel} from '../models';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import {HANDLE_SERIOUS_ERROR} from '@/store/app/actionTypes';

import Toast from '@/shared/services/toast';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import Banner from '@/shared/components/Banner.vue';
import CollapsibleSection from '@/shared/components/CollapsibleSection.vue';
import CapableAppPicker from '@/shared/components/capableAppPicker/CapableAppPicker.vue';
import PageHeader from '@/shared/components/pageHeader/PageHeader.vue';
import ReadonlyCopyableField from '@/shared/components/ReadonlyCopyableField.vue';
import {HomescreenAppCache} from '../services/homescreenAppCache';
import TagsInput from '@/shared/components/TagsInput.vue';
import {TypicalResponseTimeOptions} from '../models/typicalResponseTimeOptions';
import { ConversationMessage } from '../models/conversation-message';
import { MessageDirection } from '../models/messageDirection';
import { MessageStatus } from '../models/messageStatus';

@Component({
	components: {
		PageHeader,
		Banner,
		LoadingSpinner,
		WebAssistantPreview,
		WebAssistantViewToggle,
		CollapsibleSection,
		ImageUpload,
		CapableAppPicker,
		TagsInput,
		ReadonlyCopyableField
	},
	mixins: [validationMixin],
	validations: {
		viewModel: {
			greetingText: {required, maxLength: maxLength(25)},
			introductionText: {required, maxLength: maxLength(40)}
		}
	}
})
export default class WebAssistant extends Vue {
	@Getter public currentWorkspace!: WorkspaceViewModel;
	@Action(HANDLE_SERIOUS_ERROR) public handleSeriousError: any;

	public activeCollapseId: number = 1;

	public viewModel: WebAssistantViewModel = {
		theme: {
			brandColour: '#062B80',
			sentMessageColour: '#062B80',
			actionColour: '#062B80'
		},
		headerText: '',
		typicalResponseTime: 0,
		whitelistedDomains: [],
		homescreenApps: [],
		imageUrl: '',
		greetingText: 'Hi there 👋',
		introductionText: 'We\'re here to help. Ask us anything',
		useDefaultLauncher: true
	};

	public identityVerificationSecret: string = '';

	public initialisedHomescreenApps: HomescreenAppInitViewModel[] = [];

	public imageUploading: boolean = false;
	public webAssistantAppLoaded: boolean = false;
	public webAssistantAppInstallError: boolean = false;
	public reinstallInProgress: boolean = false;
	public updateInProgress: boolean = false;

	public typicalResponseTimeOptions = TypicalResponseTimeOptions;

	public sampleMessages: ConversationMessage[] = [
		{
			id: '1',
			type: 'text',
			direction: MessageDirection.Inbound,
			status: MessageStatus.DeliverySucceeded,
			timestampUtc: new Date(),
			text: 'Hi there! Is there anything we can help you with?'
		},
		{
			id: '2',
			type: 'text',
			direction: MessageDirection.Outbound,
			status: MessageStatus.DeliverySucceeded,
			timestampUtc: new Date(),
			text: 'Hi, yes there is...'
		}
	];

	public activeView: 'homescreen' | 'conversation' = 'homescreen';

	private codeSnippet: string = '';

	private homescreenAppCache: HomescreenAppCache;

	// Generate an error message for an input
	public getErrorMessage($v: any, prop: string, label: string) {
		if (this.invalidRequired($v, prop)) {
			return `${label} is required`;
		}
		if (this.invalidMaxLength($v, prop)) {
			return `${label} must be a maximum of ${$v.viewModel[prop].$params.maxLength.max} characters`;
		}
		return null;
	}

	public async mounted() {
		if (!this.currentWorkspace) {
			this.handleSeriousError('No workspace selected');
			return;
		}

		this.homescreenAppCache = new HomescreenAppCache();

		try {
			const response = await webAssistantApi.getInstalledApp(this.currentWorkspace.id);
			this.viewModel = response as WebAssistantViewModel;
			this.homescreenAppsChanged(this.viewModel.homescreenApps);
			this.identityVerificationSecret = response.identityVerificationSecret;
			this.webAssistantAppLoaded = true;
		} catch (error) {
			if (error.status === 404) {
				this.webAssistantAppInstallError = true;
				this.viewModel.headerText = this.currentWorkspace.name;
				this.webAssistantAppLoaded = true;
				return;
			}
			this.handleSeriousError(error.message);
		}

		const data: any = await AssetService.loadCodeSnippet();
		this.codeSnippet = data.replace('<workspace-id>', this.currentWorkspace.id);
	}

	public reinstall() {
		if (this.reinstallInProgress) {
			return;
		}

		this.reinstallInProgress = true;

		webAssistantAppService.fullInstall(this.currentWorkspace.id, this.currentWorkspace.name, this.viewModel)
			.then(response => {
				this.viewModel = response as WebAssistantViewModel;
				this.identityVerificationSecret = response.identityVerificationSecret;
				Toast.showSuccess('Web Assistant has been successfully configured');
				this.webAssistantAppInstallError = false;
			})
			.catch(error => {
				Toast.showError(error.message);
			})
			.finally(() => {
				this.reinstallInProgress = false;
			});
	}

	public update() {
		if (this.$v.$invalid) {
			return;
		}

		if (this.imageUploading) {
			Toast.showInfo('Cannot save while an image is being uploaded, please try again');
			return;
		}

		if (!this.updateInProgress && !this.reinstallInProgress) {
			this.updateInProgress = true;

			webAssistantApi.updateWebAssistant(this.currentWorkspace.id, this.viewModel)
				.then(() => Toast.showSuccess('Successfully updated Web Assistant'))
				.catch(err => Toast.showError(err.message))
				.finally(() => this.updateInProgress = false);
		}
	}

	public onImageSelected() {
		this.imageUploading = true;
	}

	public onImageUploaded(imageUrl: string) {
		this.viewModel.imageUrl = imageUrl;
		this.imageUploading = false;
	}

	public onImageCleared() {
		this.viewModel.imageUrl = '';
		this.imageUploading = false;
	}

	public onCodeSnippetCopy() {
		Toast.showInfo('Copied snippet to clipboard');
	}

	public getCodeSnippet() {
		return '<script>' + this.codeSnippet + '<\/script>';
	}

	public getInitSnippet() {
		return `<script>Connect.init({ workspaceId: '${this.currentWorkspace.id}' });<\/script>`;
	}

	public configRequest(workspaceId: string, installedAppId: string, inputData: any, storedData: any, componentId: any) {
		return webAssistantApi.homescreenAppConfig(workspaceId, installedAppId, inputData, storedData, componentId);
	}

	public homescreenAppsChanged(apps: HomescreenAppViewModel[]) {
		const promises = apps.map(app => {
			return this.homescreenAppCache.initialiseApp(this.currentWorkspace.id, app)
				.then(res => {
					return {configured: app, initialised: res};
				})
				.catch(error => {
					Toast.showError('Failed to load homescreen app');
					const init: HomescreenAppInitViewModel = {
						id: `${app.installedAppId}`,
						card: {
							components: []
						}
					};
					return {configured: app, initialised: init};
				});
		});

		Promise.all(promises).then(res => {
			this.viewModel.homescreenApps = res.map(x => x.configured);
			this.initialisedHomescreenApps = res.map(x => x.initialised);
		});
	}

	public messageLoad($state: any) {
		$state.loaded();
		$state.complete();
	}

	public handleAppUpdate(app: HomescreenAppViewModel, appIndex: number) {
		this.viewModel.homescreenApps[appIndex] = app;
		this.homescreenAppsChanged(this.viewModel.homescreenApps);
	}

	// Whether an input is currently invalid because it is required and empty.
	private invalidRequired($v: any, prop: string) {
		return !$v.viewModel[prop].required && $v.viewModel[prop].$invalid && $v.viewModel[prop].$dirty;
	}

	// Whether an input is currently invalid because it is longer than the maximum allowed.
	private invalidMaxLength($v: any, prop: string) {
		return !$v.viewModel[prop].maxLength && $v.viewModel[prop].$invalid && $v.viewModel[prop].$dirty;
	}
}
